import { Grid, Typography, Box, InputLabel, MenuItem, Select, Icon } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { visuallyHidden } from "@mui/utils"
import { OreveButton } from "components/button/OreveButton.tsx"
import { StyledTextField } from "components/inputs/StyledTextField.tsx"
import { AutochargeVidSection } from "layouts/card-holder/composants/autochargeVidSection.tsx"
import { useForm, Controller } from "react-hook-form"
import { useMmsService } from "services/mms/context/MmsServiceContext.tsx"
import React, { useCallback, useEffect, useState } from "react"
import {
    AccountInfo,
    ConfirmedVids,
    DriverInfo,
    ListCardHolderInfo,
    ListDriverInfo,
    VehicleInfo,
} from "types/list-card-holder"
import CheckIcon from "assets/svg/check-icon.svg"
import ErrorIcon from "assets/svg/error-icon.svg"
import { findDuplicateDriver } from "layouts/card-holder/utils/functions.ts"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import BorderColorIcon from "@mui/icons-material/BorderColor"
import {
    OrevePopup,
    OrevePopupContent,
    OrevePopupTitle,
} from "components/popup/OrevePopup.tsx"
import { useCardHolderContext } from "layouts/card-holder/context/useCardHolderContext.ts"
import { AddDriverPopupIcon } from "layouts/card-holder/drivers/icons.tsx"
import { SelectChangeEvent } from "@mui/material/Select"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import Truck from "assets/svg/truck.svg?react"
import { useFeatureFlags } from "context/FeatureFlagsContext"

interface FeedbackPopupProps {
    success: boolean
    handleClose: () => void
    setDisplayResponse: (value: boolean) => void
    isVehicle?: boolean | null
}

export interface IAddDriverFormProps {
    firstName: string
    lastName: string
    email: string
}

export const AddCardHolderPopup = ({
    onVidCreationError,
}: {
    onVidCreationError?: (vids: string[]) => void
}) => {
    const {
        openAddCardHolder: open,
        onClosePopup: handleClose,
        listDrivers: drivers,
        fetchListDrivers,
        isVehicle: isVehicle,
        listVehicles: vehicles,
    } = useCardHolderContext()

    const { cardHolderService } = useMmsService()
    const { useAddCardHolder, usePostAutochargeToVehicle } = cardHolderService
    const {
        success,
        isLoading: addCardHolderIsLoading,
        sendRequest: addCardHolder,
    } = useAddCardHolder()
    const {
        isLoading: autochargeIsLoading,
        sendRequest: addAutochargeToVehicle,
    } = usePostAutochargeToVehicle()
    const [displayResponse, setDisplayResponse] = useState(false)
    const [displayWarningDriver, setDisplayWarningDriver] = useState(false)
    const [driverInfo, setDriverInfo] = useState<IAddDriverFormProps>({
        firstName: "",
        lastName: "",
        email: "",
    })

    const getFormData = useCallback(
        (postDriver: IAddDriverFormProps) => {
            setDriverInfo(postDriver)
        },
        [driverInfo]
    )

    const onAddDriver = useCallback(
        async (postDriver: IAddDriverFormProps, existed: boolean) => {
            if (existed) {
                getFormData(postDriver)
                setDisplayWarningDriver(true)
            } else {
                await addCardHolder(postDriver)
                setDisplayResponse(true)
                await fetchListDrivers()
            }
        },
        [addCardHolder, fetchListDrivers]
    )

    const onAddVehicle = useCallback(
        async (
            postVehicle: IAddDriverFormProps,
            vehicleVids: AutochargeStatus[],
            existed: boolean
        ) => {
            if (existed) {
                getFormData(postVehicle)
                setDisplayWarningDriver(true)
                return
            }

            const created = (await addCardHolder(
                postVehicle
            )) as AccountInfo | null

            if (!created) {
                setDisplayResponse(true)
                return
            }

            const rawId = created?.info?.id
            const userId = rawId ?? null
            setDisplayResponse(true)

            void (async () => {
                const vidsWithUserId = vehicleVids.map((vid) => ({
                    ...vid,
                    userId,
                }))

                const failedVids: string[] = []

                for (const vid of vidsWithUserId) {
                    const result = await addAutochargeToVehicle(vid)

                    if (result == null && vid.idTagUid) {
                        failedVids.push(vid.idTagUid)
                    }
                }

                if (failedVids.length > 0) {
                    onVidCreationError?.(failedVids)
                }

                await fetchListDrivers()
            })()
        },
        [addCardHolder, fetchListDrivers, getFormData, addAutochargeToVehicle]
    )

    useEffect(() => {
        if (open) {
            setDisplayResponse(false)
            setDisplayWarningDriver(false)
        }
    }, [open])

    const popupContent = () => {
        if (displayWarningDriver) {
            return (
                <DuplicateDriverPopup
                    setDisplayResponse={setDisplayResponse}
                    setDisplayWarningDriver={setDisplayWarningDriver}
                    onAddDriver={onAddDriver}
                    driverInfo={driverInfo}
                />
            )
        }
        if (displayResponse) {
            const PopupFeedback = isVehicle
                ? AddVehiclePopupFeedback
                : AddDriverPopupFeedback
            return (
                <PopupFeedback
                    success={success ?? false}
                    handleClose={handleClose}
                    setDisplayResponse={setDisplayResponse}
                />
            )
        } else {
            return isVehicle ? (
                <AddVehiclePopupMain
                    onAddVehicle={onAddVehicle}
                    isLoading={addCardHolderIsLoading || autochargeIsLoading}
                    drivers={drivers}
                    currentDriver={driverInfo}
                    vehicles={vehicles}
                />
            ) : (
                <AddDriverPopupMain
                    onAddDriver={onAddDriver}
                    isLoading={addCardHolderIsLoading}
                    drivers={drivers}
                    currentDriver={driverInfo}
                />
            )
        }
    }

    return (
        <OrevePopup open={open} handleClose={() => handleClose()}>
            {popupContent()}
        </OrevePopup>
    )
}

export const AddDriverPopupMain = ({
    onAddDriver,
    isLoading,
    drivers,
    currentDriver,
}: {
    onAddDriver: (data: IAddDriverFormProps, existed: boolean) => void
    isLoading: boolean
    drivers: ListDriverInfo
    currentDriver: IAddDriverFormProps
}) => {
    return (
        <Box data-cy={"popup_drivers__add-driver--main"}>
            <OrevePopupTitle>
                <AddPopupTitle />
            </OrevePopupTitle>
            <OrevePopupContent>
                <AddDriverForm
                    onAddDriver={onAddDriver}
                    isLoading={isLoading}
                    cardHolders={drivers}
                    currentDriver={currentDriver}
                />
            </OrevePopupContent>
        </Box>
    )
}

const AddDriverForm = ({
    onAddDriver,
    isLoading,
    cardHolders,
    currentDriver,
}: {
    onAddDriver: (data: IAddDriverFormProps, existed: boolean) => void
    isLoading: boolean
    cardHolders: ListCardHolderInfo
    currentDriver: IAddDriverFormProps
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            firstName: currentDriver.firstName ? currentDriver.firstName : "",
            lastName: currentDriver.lastName ? currentDriver.lastName : "",
            email: currentDriver.email ? currentDriver.email : "",
        },
        reValidateMode: "onSubmit",
    })
    const drivers = cardHolders.filter(
        (item): item is DriverInfo => "firstName" in item && "lastName" in item
    )

    const theme = useTheme()
    const onSubmitAddForm = (data: IAddDriverFormProps) => {
        const formattedDriver = {
            lastName: data.lastName.trim(),
            firstName: data.firstName.trim(),
            email: data.email.trim(),
            type: "driver",
        }
        const duplicateDriver = findDuplicateDriver(
            formattedDriver.firstName,
            formattedDriver.lastName,
            drivers
        )
        if (duplicateDriver) {
            onAddDriver(formattedDriver, true)
        } else {
            onAddDriver(formattedDriver, false)
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmitAddForm)}>
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                        name="lastName"
                        control={control}
                        rules={{
                            required: true,
                            validate: (value) => {
                                if (!value || value.trim().length === 0) {
                                    return false
                                }
                                const firstnameWithoutSpaces = value.replace(
                                    /\s+/g,
                                    ""
                                )
                                return firstnameWithoutSpaces.length >= 2
                            },
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-page-lastName"
                                data-cy={"form_drivers__add-driver--last-name"}
                                label="Nom"
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.lastName}
                                helperText={
                                    errors.lastName ? "Ce champ est requis" : ""
                                }
                                {...field}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            required: true,
                            validate: (value) => {
                                if (!value || value.trim().length === 0) {
                                    return false
                                }
                                const firstnameWithoutSpaces = value.replace(
                                    /\s+/g,
                                    ""
                                )
                                return firstnameWithoutSpaces.length >= 2
                            },
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-page-firstName"
                                data-cy={"form_drivers__add-driver--first-name"}
                                label="Prénom"
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.firstName}
                                helperText={
                                    errors.firstName
                                        ? "Ce champ est requis"
                                        : ""
                                }
                                {...field}
                            />
                        )}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: true,
                            validate: (value) => {
                                const duplicateEmail = drivers.find(
                                    (driver) => driver.email === value.trim()
                                )
                                if (
                                    !value.match(
                                        /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/
                                    )
                                ) {
                                    return "Le format de l'email n'est pas conforme."
                                }

                                if (duplicateEmail) {
                                    return "Un conducteur avec cet email existe déjà."
                                }
                            },
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-page-email"
                                data-cy={"form_drivers__add-driver--email"}
                                label="Email"
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.email}
                                helperText={
                                    errors.email &&
                                    (errors.email?.type === "required"
                                        ? "Ce champ est requis"
                                        : errors.email.message)
                                }
                                {...field}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <OreveButton
                type="submit"
                data-cy="form_drivers__add-driver--save-change"
                value="Ajouter un conducteur"
                variant="contained"
                color="primary"
                loading={isLoading}
                sx={{ mt: "2.25rem" }}
            />
        </form>
    )
}

const AddPopupTitle = () => {
    return (
        <>
            <AddDriverPopupIcon />
            <Typography
                variant="headlineSmall"
                component="div"
                data-cy={"form_drivers__add-driver--title"}
            >
                Ajouter un conducteur
            </Typography>
        </>
    )
}

export const AddDriverPopupFeedback = ({
    success,
    handleClose,
    setDisplayResponse,
}: FeedbackPopupProps) => {
    return (
        <Box data-cy={"popup_drivers__add-drivers--success"}>
            <OrevePopupTitle>
                <FeedbackPopupTitle success={success} />
            </OrevePopupTitle>
            <OrevePopupContent>
                <FeedbackPopupContent
                    success={success}
                    handleClose={handleClose}
                    setDisplayResponse={setDisplayResponse}
                    //  isVehicle={false}
                />
            </OrevePopupContent>
        </Box>
    )
}

export const AddVehiclePopupFeedback = ({
    success,
    handleClose,
    setDisplayResponse,
}: FeedbackPopupProps) => {
    return (
        <Box data-cy={" popup_vehicles__add-drivers--success"}>
            <OrevePopupTitle>
                <FeedbackPopupTitle success={success} isVehicle={true} />
            </OrevePopupTitle>
            <OrevePopupContent>
                <FeedbackPopupContent
                    success={success}
                    handleClose={handleClose}
                    setDisplayResponse={setDisplayResponse}
                    isVehicle={true}
                />
            </OrevePopupContent>
        </Box>
    )
}

const FeedbackPopupTitle = ({
    success,
    isVehicle = null,
}: {
    success: boolean
    isVehicle?: boolean | null
}) => {
    const text =
        isVehicle === null
            ? "conducteur"
            : isVehicle
              ? "véhicule"
              : "conducteur"
    return (
        <>
            {!success ? (
                <Icon
                    sx={{
                        color: "error.main",
                        width: "37px",
                        height: "37px",
                    }}
                    component={ErrorOutlineIcon}
                />
            ) : (
                <img src={CheckIcon} alt={"valid check"} />
            )}
            <Typography variant="headlineSmall" component="div">
                {!success
                    ? `Le ${text} n'a pas pu être ajouté`
                    : `Le ${text} a été ajouté avec succès`}
            </Typography>
        </>
    )
}

const FeedbackPopupContent = ({
    success,
    handleClose,
    setDisplayResponse,
    isVehicle = null,
}: FeedbackPopupProps) => {
    const entityType =
        isVehicle === null || !isVehicle ? "conducteur" : "véhicule"

    const identifierLabel =
        isVehicle === null || !isVehicle
            ? " cet mail"
            : " cette plaque d'immatriculation"

    return (
        <>
            {!success ? (
                <Typography variant="body">
                    Une erreur est survenue lors de l'ajout du {entityType}.
                    Vérifiez qu'il n'existe pas déjà un {entityType} avec
                    {identifierLabel}.
                </Typography>
            ) : null}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.6rem",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <OreveButton
                    value={`Ajouter un autre ${entityType}`}
                    variant="outlined"
                    color="primary"
                    onClick={() => setDisplayResponse(false)}
                    sx={{ mt: "1.5rem" }}
                />
                <OreveButton
                    value={`Retourner à la liste des ${entityType}s`}
                    data-cy={`popup_${entityType == "conducteur" ? "drivers" : "vehicles"}__add-${entityType === "conducteur" ? "drivers" : "vehicles"}--feedback`}
                    variant="contained"
                    color="primary"
                    onClick={() => handleClose()}
                    sx={{ mt: "1.5rem" }}
                />
            </Box>
        </>
    )
}

export const DuplicateDriverPopup = ({
    setDisplayResponse,
    setDisplayWarningDriver,
    onAddDriver,
    driverInfo,
}: {
    setDisplayResponse: React.Dispatch<React.SetStateAction<boolean>>
    setDisplayWarningDriver: React.Dispatch<React.SetStateAction<boolean>>
    onAddDriver: (data: IAddDriverFormProps, existed: boolean) => void
    driverInfo: IAddDriverFormProps
}) => {
    return (
        <>
            <OrevePopupTitle>
                <img src={ErrorIcon} alt={"error-icon"} />
                <Typography
                    display="block"
                    sx={{
                        fontSize: "1.5rem",
                        fontWeight: 400,
                        lineHeight: "2rem",
                        letterSpacing: "0em",
                        textAlign: "center",
                    }}
                    color="error"
                >
                    {`Le conducteur ${driverInfo.firstName} ${driverInfo.lastName} existe déjà sous une autre adresse mail`}
                </Typography>
            </OrevePopupTitle>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "1.5rem",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <OreveButton
                    icon={ArrowForwardIcon}
                    value="Poursuivre la création"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                        onAddDriver(driverInfo, false)
                        setDisplayResponse(false)
                        setDisplayWarningDriver(false)
                    }}
                    sx={{ mt: "1.5rem" }}
                />
                <OreveButton
                    icon={BorderColorIcon}
                    value="Modifier le conducteur"
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setDisplayResponse(false)
                        setDisplayWarningDriver(false)
                    }}
                    sx={{ mt: "1.5rem" }}
                />
            </Box>
        </>
    )
}

export const AddVehiclePopupMain = ({
    onAddVehicle,
    isLoading,
    drivers,
    currentDriver,
    vehicles,
}: {
    onAddVehicle: (
        data: IAddDriverFormProps,
        vids: AutochargeStatus[],
        existed: boolean
    ) => void
    isLoading: boolean
    drivers: ListDriverInfo
    currentDriver: IAddDriverFormProps
    vehicles: VehicleInfo[]
}) => {
    const theme = useTheme()
    return (
        <Box
            data-cy={"popup_vehicle__add-vehicle--main"}
            sx={{
                display: "flex",
                flexDirection: "column",
                maxHeight: "80vh",
                overflow: "hidden",
            }}
        >
            <OrevePopupTitle>
                <Box
                    data-cy={"popup_vehicle__add-vehicle--title"}
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "0.5rem",
                        justifyContent: "center",
                    }}
                >
                    <Truck
                        style={{
                            color: theme.palette.success.main,
                            marginRight: 20,
                        }}
                    />
                    <Typography variant="headlineSmall" component="div">
                        Ajouter un véhicule
                    </Typography>
                </Box>
            </OrevePopupTitle>
            <OrevePopupContent>
                <AddVehicleForm
                    onAddVehicle={onAddVehicle}
                    isLoading={isLoading}
                    drivers={drivers}
                    currentDriver={currentDriver}
                    listVehicles={vehicles}
                />
            </OrevePopupContent>
        </Box>
    )
}

type AddVehicleFormValues = {
    email: string
    firstName: string
    vids: { uid: string; uidConfirm?: string; status: boolean }[]
}

interface AutochargeStatus {
    idTagUid: string | null
    userId: number | null
    autoChargeEnabled: boolean | null
}

const AddVehicleForm = ({
    onAddVehicle,
    isLoading,
    drivers,
    currentDriver,
    listVehicles,
}: {
    onAddVehicle: (
        data: IAddDriverFormProps,
        vids: AutochargeStatus[],
        existed: boolean
    ) => void
    isLoading: boolean
    drivers: ListDriverInfo
    currentDriver: IAddDriverFormProps
    listVehicles: VehicleInfo[]
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        setError,
        clearErrors,
        getValues,
        setValue,
    } = useForm<AddVehicleFormValues>({
        defaultValues: {
            email: currentDriver.email ?? "",
            firstName: currentDriver.firstName ?? "",
            vids: [{ uid: "", uidConfirm: "", status: true }],
        },
    })

    const { vidAutochargeEnabled } = useFeatureFlags()
    const [confirmedVids, setConfirmedVids] = useState<ConfirmedVids>([])
    const theme = useTheme()
    const gridSize = { xs: 12, sm: 6 }
    const onSubmitAddForm = (data: AddVehicleFormValues) => {
        const formattedDriver = {
            email: data.email.replace(/\s/g, ""),
            lastName: data.email.replace(/\s/g, ""),
            firstName: data.firstName.trim(),
            type: "vehicle",
            licensePlateType: selectImmatType,
        }
        const cleanedVids: AutochargeStatus[] = confirmedVids
            .filter(({ vid }) => vid.trim() !== "")
            .map(({ vid, enabled }) => ({
                idTagUid: vid.trim(),
                userId: null,
                autoChargeEnabled: enabled ?? true,
            }))

        const duplicateDriver = drivers.find(
            (driver) => driver.email === formattedDriver.email
        )
        if (duplicateDriver) {
            onAddVehicle(formattedDriver, cleanedVids, true)
        } else {
            onAddVehicle(formattedDriver, cleanedVids, false)
        }
    }
    const [selectImmatType, setSelectImmatType] = useState<string>("France")
    const [immat, setImmat] = useState<string>("")
    const regex = /^(?:\d{1,4}-[A-Z]{1,3}-\d{2}|[A-Z]{2}-\d{3}-[A-Z]{2})$/
    const formatInput = (value: string) => {
        const cleanedInput = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()

        if (!cleanedInput) return ""

        //  0..0000 - X..XXX - 00
        if (/^\d/.test(cleanedInput)) {
            let i = 0

            const part1Match = cleanedInput.slice(i).match(/^\d{1,4}/)
            const part1 = part1Match?.[0] ?? ""
            i += part1.length

            const part2Match = cleanedInput.slice(i).match(/^[A-Z]{1,3}/)
            const part2 = part2Match?.[0] ?? ""
            i += part2.length

            const part3Match = cleanedInput.slice(i).match(/^\d{0,2}/)
            const part3 = part3Match?.[0] ?? ""

            return [part1, part2, part3].filter(Boolean).join("-")
        }

        // XX-000-XX
        if (/^[A-Z]/.test(cleanedInput)) {
            let i = 0

            const part1Match = cleanedInput.slice(i).match(/^[A-Z]{1,2}/)
            const part1 = part1Match?.[0] ?? ""
            i += part1.length

            const part2Match = cleanedInput.slice(i).match(/^\d{1,3}/)
            const part2 = part2Match?.[0] ?? ""
            i += part2.length

            const part3Match = cleanedInput.slice(i).match(/^[A-Z]{0,2}/)
            const part3 = part3Match?.[0] ?? ""

            return [part1, part2, part3].filter(Boolean).join("-")
        }
        // If neither pattern matches, keep the cleaned input
        return cleanedInput
    }
    useEffect(() => {
        // Only manage the "onChange" format error this effect itself sets —
        // blindly calling clearErrors("email") here would also wipe out
        // other error types (e.g. "required" from a submit attempt) the
        // instant they're set, since this effect can re-fire on unrelated
        // formState updates.
        if (selectImmatType === "France" && immat.length > 0 && !regex.test(immat)) {
            if (errors.email?.type !== "onChange") {
                setError("email", {
                    type: "onChange",
                    message: "La plaque d'immatriculation n'est pas conforme",
                })
            }
        } else if (errors.email?.type === "onChange") {
            clearErrors("email")
        }
    }, [immat, selectImmatType, setError, clearErrors, errors.email])

    const handleChange = (event: SelectChangeEvent) => {
        setSelectImmatType(event.target.value)
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmitAddForm)}
            style={{ paddingBottom: "10px" }}
        >
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 12 }}>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            maxLength: 30,
                            required: true,
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-page-description"
                                label="Nom du véhicule"
                                required
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.firstName}
                                helperText={
                                    errors.firstName
                                        ? errors.firstName.type === "maxLength"
                                            ? "Le nom du véhicule doit faire au maximum 30 caractères"
                                            : ""
                                        : ""
                                }
                                {...field}
                                data-cy={
                                    "form_vehicle__add-vehicle--description"
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid size={gridSize}>
                    <InputLabel id="type-matricule-select-label" sx={visuallyHidden}>
                        Pays
                    </InputLabel>
                    <Select
                        fullWidth
                        sx={{
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            ".MuiOutlinedInput-notchedOutline": {
                                border: "none",
                            },
                            // Focus outline
                        }}
                        labelId="type-matricule-select-label"
                        id="type-matricule"
                        value={selectImmatType}
                        onChange={handleChange}
                        data-cy={
                            "form_vehicle__add-vehicle--license-plate-type"
                        }
                    >
                        <MenuItem
                            value="France"
                            data-cy={
                                "form_vehicle__add-vehicle--license-plate-type-france"
                            }
                        >
                            France
                        </MenuItem>
                        <MenuItem
                            value="Autre"
                            data-cy={
                                "form_vehicle__add-vehicle--license-plate-type-other"
                            }
                        >
                            Autre
                        </MenuItem>
                    </Select>
                </Grid>
                <Grid size={gridSize}>
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: true,
                            minLength: 4,
                            maxLength: 15,
                            validate: (value) => {
                                const duplicateLicensePlate = listVehicles.find(
                                    (vehicle) =>
                                        vehicle.licensePlate.toLowerCase() ===
                                        value.trim().toLowerCase()
                                )
                                if (duplicateLicensePlate) {
                                    return "Une plaque d'immatriculation avec cette valeur existe déjà."
                                }
                                if (selectImmatType === "France") {
                                    if (!regex.test(value)) {
                                        return "La plaque d'immatriculation n'est pas conforme"
                                    }
                                }
                                return true
                            },
                        }}
                        render={({ field }) => (
                            <StyledTextField
                                id="drivers-page-licensePlate"
                                label="Plaque d'immatriculation"
                                required
                                bgcolor={theme.palette.grey[100]}
                                error={!!errors.email}
                                helperText={
                                    errors.email
                                        ? errors.email.type === "required"
                                            ? "Ce champ est requis"
                                            : errors.email.type === "minLength"
                                              ? "La plaque d'immatriculation doit faire au moins 5 caractères"
                                              : errors.email.type ===
                                                  "maxLength"
                                                ? "La plaque d'immatriculation doit faire au maximum 15 caractères"
                                                : errors.email.message
                                        : ""
                                }
                                {...field}
                                value={immat}
                                onChange={(e) => {
                                    const raw = e.target.value
                                    const next =
                                        selectImmatType === "France"
                                            ? formatInput(raw.toUpperCase())
                                            : raw
                                    setImmat(next)
                                    field.onChange(next)
                                }}
                                data-cy={
                                    "form_vehicle__add-vehicle--license-plate"
                                }
                            />
                        )}
                    />
                    {selectImmatType == "France" && errors.email == null && (
                        <Typography
                            color="grey.500"
                            variant="labelLarge"
                            component="div"
                            align={"left"}
                        >
                            FNI(1234 AB 75) ou SIV(AA-123-AA)
                        </Typography>
                    )}
                </Grid>
                {vidAutochargeEnabled && (
                    <Grid size={{ xs: 12, sm: 12 }}>
                        <AutochargeVidSection
                            control={control}
                            errors={errors}
                            setError={setError}
                            clearErrors={clearErrors}
                            getValues={getValues}
                            setValue={setValue}
                            confirmedVids={confirmedVids}
                            setConfirmedVids={setConfirmedVids}
                            max={4}
                            index={0}
                            isUpdateMode={false}
                            isVehicleInactive={true}
                        />
                    </Grid>
                )}
            </Grid>
            <OreveButton
                type="submit"
                value="Ajouter le véhicule"
                variant="contained"
                color="primary"
                loading={isLoading}
                sx={{
                    mt: "2.25rem",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                }}
                data-cy={"form_vehicle__add-vehicle--submit"}
            />
        </form>
    )
}
